import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import {
    INDUSTRY_USE_CASES,
    WidgetType,
    WIDGET_TYPE_BADGE,
    WIDGET_TYPE_LABEL,
} from "../data/industryUseCases";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import { buildCollectionPageSchema } from "../data/seo/structuredData";
import { comMarketing } from "../data/seo/kw3OnPage";

/**
 * /use-cases — vertical-pack gallery. Mirrors the layout of the
 * dashboard's VerticalPackGallery so visitors see the same tile design
 * they'll encounter at onboarding. Each card links to the per-industry
 * landing page at /use-cases/:slug.
 */

const FILTERS: Array<{ id: WidgetType | "all"; label: string }> = [
    { id: "all", label: "All packs" },
    { id: "lead_generation", label: "Lead generation" },
    { id: "appointment_booking", label: "Appointment booking" },
    { id: "customer_support", label: "Customer support" },
];

const SEO_TITLE = "AI Voice & Chat Use Cases by Industry — FloGPT";
const SEO_DESCRIPTION =
    "Browse FloGPT vertical packs across 13 industries — auto dealers, healthcare, education, fitness, hospitality, real estate, restaurants, retail and more. Each pack is a pre-tuned AI voice + chat agent.";
const SEO_KEYWORDS = [
    "AI use cases",
    "industry chatbot",
    "AI voice agent industries",
    "vertical pack AI",
    "AI for healthcare",
    "AI for real estate",
    "AI for restaurants",
];

export default function UseCasesIndex() {
    const [filter, setFilter] = useState<WidgetType | "all">("all");

    usePageSeo({
        title: SEO_TITLE,
        description: SEO_DESCRIPTION,
        keywords: SEO_KEYWORDS.join(", "),
        canonical: "/use-cases",
    });

    const collectionSchema = useMemo(
        () =>
            buildCollectionPageSchema({
                name: "FloGPT vertical packs by industry",
                description: SEO_DESCRIPTION,
                path: "/use-cases",
                itemCount: INDUSTRY_USE_CASES.length,
            }),
        [],
    );

    const breadcrumbSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Use Cases", item: `${marketingOrigin()}/use-cases` },
            ],
        }),
        [],
    );

    // ItemList schema is the canonical way to mark up a directory of pages —
    // Google can use it to surface the gallery as a carousel rich result.
    const itemListSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "FloGPT industry use cases",
            numberOfItems: INDUSTRY_USE_CASES.length,
            itemListElement: INDUSTRY_USE_CASES.map((industry, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${marketingOrigin()}/use-cases/${industry.slug}`,
                name: industry.shortLabel,
            })),
        }),
        [],
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const filteredIndustries = useMemo(() => {
        if (filter === "all") return INDUSTRY_USE_CASES;
        return INDUSTRY_USE_CASES.filter((i) => i.callWidgetType === filter);
    }, [filter]);

    const totalsByType = useMemo(() => {
        return INDUSTRY_USE_CASES.reduce<Record<WidgetType, number>>(
            (acc, ind) => {
                acc[ind.callWidgetType] = (acc[ind.callWidgetType] || 0) + 1;
                return acc;
            },
            { lead_generation: 0, appointment_booking: 0, customer_support: 0 },
        );
    }, []);

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                {/* Hero */}
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-orange-500/15 blur-3xl" />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
                        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="max-w-3xl"
                        >
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Use Cases · Industry packs
                            </div>
                            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                                AI Voice + Chat,{" "}
                                <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                                    pre-tuned for your industry
                                </span>
                            </h1>
                            <p className="mt-6 text-lg leading-relaxed text-slate-300">
                                {INDUSTRY_USE_CASES.length} vertical packs ship with the prompts, scripts,
                                intent gates and post-call triggers your business actually uses. Pick a pack,
                                see the demo, deploy in minutes.
                            </p>

                            <div className="mt-7 flex flex-wrap items-center gap-3 text-[11px]">
                                {(Object.keys(totalsByType) as WidgetType[]).map((wt) => (
                                    <span
                                        key={wt}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-slate-300"
                                    >
                                        <span
                                            className={`h-2 w-2 rounded-full bg-gradient-to-r ${WIDGET_TYPE_BADGE[wt]}`}
                                        />
                                        <span className="font-semibold text-white">{totalsByType[wt]}</span>
                                        <span>{WIDGET_TYPE_LABEL[wt]}</span>
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Filter chips */}
                <section className="relative">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="flex flex-wrap items-center gap-2 mb-8">
                            {FILTERS.map((f) => {
                                const isActive = filter === f.id;
                                return (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => setFilter(f.id)}
                                        className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
                                            isActive
                                                ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg"
                                                : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
                                        }`}
                                    >
                                        {f.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Card grid */}
                <section className="relative pb-20">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredIndustries.map((industry, idx) => {
                                const Icon = industry.Icon;
                                return (
                                    <motion.div
                                        key={industry.key}
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.3) }}
                                    >
                                        <Link
                                            to={`/use-cases/${industry.slug}`}
                                            className="group relative block h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all overflow-hidden"
                                        >
                                            <motion.div
                                                className={`pointer-events-none absolute -inset-4 rounded-3xl blur-2xl opacity-0 group-hover:opacity-60 transition-opacity ${industry.softAccent}`}
                                            />
                                            <div className="relative">
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${industry.accent} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                                                        <Icon className="h-6 w-6 text-white" />
                                                    </div>
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r ${
                                                            WIDGET_TYPE_BADGE[industry.callWidgetType]
                                                        } bg-clip-text text-transparent text-right`}
                                                    >
                                                        {WIDGET_TYPE_LABEL[industry.callWidgetType]}
                                                    </span>
                                                </div>

                                                <h3 className="text-xl font-bold text-white group-hover:text-orange-400 transition-colors">
                                                    {comMarketing() && industry.slug === "home-services"
                                                        ? "Home services AI"
                                                        : comMarketing() && industry.slug === "insurance"
                                                          ? "Insurance chat platform"
                                                          : industry.shortLabel}
                                                </h3>
                                                <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-3">
                                                    {industry.tagline}
                                                </p>

                                                <div className="mt-4 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
                                                    {industry.pipelineSteps.map((s, i, arr) => (
                                                        <span key={s} className="inline-flex items-center gap-1.5">
                                                            <span className="rounded-md bg-slate-800/80 border border-white/5 px-1.5 py-0.5 text-slate-400">{s}</span>
                                                            {i < arr.length - 1 && <span className="text-slate-700">→</span>}
                                                        </span>
                                                    ))}
                                                </div>

                                                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                                                    Voice + chat demo
                                                    <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                </div>

                                                <div className="absolute top-0 right-0 text-[10px] font-mono text-slate-600 opacity-50">
                                                    {industry.key}
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {filteredIndustries.length === 0 && (
                            <div className="text-center py-16 text-slate-400">
                                No packs match this filter yet.
                            </div>
                        )}
                    </div>
                </section>

                {/* Special-mention: Shopify (existing dedicated page) */}
                <section className="relative pb-20">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-slate-900/60 backdrop-blur-sm p-6 sm:p-8 overflow-hidden"
                        >
                            <div className="pointer-events-none absolute -top-16 right-0 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
                            <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                                <div className="sm:col-span-2">
                                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                        Featured · Shopify
                                    </div>
                                    <h3 className="mt-3 text-2xl font-bold text-white">
                                        Shopify AI Customer Support
                                    </h3>
                                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                                        A dedicated end-to-end Shopify integration: order tracking, refund and
                                        return automation, and abandoned-checkout recovery — with native webhook
                                        ingest from your store.
                                    </p>
                                </div>
                                <div className="flex sm:justify-end">
                                    <Link
                                        to="/shopify-ai-customer-support"
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        Explore Shopify pack
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* CTA */}
                <section className="relative pb-24">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8">
                        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-10 sm:p-14 text-center overflow-hidden">
                            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
                            <div className="relative">
                                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    Don't see your industry?
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl mx-auto">
                                    The packs are starting points — every prompt, script, intent gate and trigger is
                                    fully customisable. Bring your own data, and we'll spin up a pack tuned to
                                    your business in a 30-minute call.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        Start free
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Talk to a human
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <JsonLd id="usecases-collection" data={collectionSchema} />
            <JsonLd id="usecases-breadcrumb" data={breadcrumbSchema} />
            <JsonLd id="usecases-itemlist" data={itemListSchema} />
        </div>
    );
}
